import type { ItemKey } from '../core/state';

/**
 * Extension points so feature modules plug into the game without editing each
 * other's dispatch code: object handlers, NPC talk, bag item uses, menu items.
 */
const objectTables: Partial<Record<string, () => void>>[] = [];
const talkers: ((id: string) => boolean)[] = [];
const itemUses = new Map<ItemKey, () => void>();
const menuItems: { label: () => string; run: () => void }[] = [];
const doorRules: ((to: string, from: string) => string | null)[] = [];

/** A rule that can close a door (returns a reason) — e.g. typhoon days. */
export function registerDoorRule(fn: (to: string, from: string) => string | null): void {
  doorRules.push(fn);
}
export function doorBlocked(to: string, from: string): string | null {
  for (const f of doorRules) {
    const r = f(to, from);
    if (r) return r;
  }
  return null;
}

export function registerObjects(table: Partial<Record<string, () => void>>): void {
  objectTables.push(table);
}
/** A handler for every object whose id starts with `prefix` (shared services: toilets, rentals, cash machines). */
const prefixHandlers: [string, (id: string) => void][] = [];
export function registerObjectPrefix(prefix: string, fn: (id: string) => void): void {
  prefixHandlers.push([prefix, fn]);
}
export function objectHandler(id: string): (() => void) | undefined {
  for (const t of objectTables) if (t[id]) return t[id];
  for (const [p, f] of prefixHandlers) if (id.startsWith(p)) return () => f(id);
  return undefined;
}

/** `fn` returns true if it handled the NPC. */
export function registerTalk(fn: (id: string) => boolean): void {
  talkers.push(fn);
}
export function talkRegistered(id: string): boolean {
  return talkers.some((f) => f(id));
}

export function registerItemUse(item: ItemKey, fn: () => void): void {
  itemUses.set(item, fn);
}
export function itemUse(item: ItemKey): (() => void) | undefined {
  return itemUses.get(item);
}

export function registerMenuItem(label: () => string, run: () => void): void {
  menuItems.push({ label, run });
}
export function extraMenuItems(): readonly { label: () => string; run: () => void }[] {
  return menuItems;
}
